import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import IEPBuilder from "@/components/IEPBuilder";
import MeetingDialog from "@/components/MeetingDialog";
import { ChevronLeft, Sparkles, Plus, Trash2, TrendingUp, CheckCircle2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const TABS = [
  { id: "iep", label: "IEP" },
  { id: "goals", label: "Goals" },
  { id: "lessons", label: "Lessons" },
  { id: "meetings", label: "Meetings" },
  { id: "supports", label: "Supports" },
  { id: "progress", label: "Progress" },
];

export default function StudentDetail() {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [iep, setIep] = useState(null);
  const [goals, setGoals] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [supports, setSupports] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [tab, setTab] = useState("iep");

  const loadAll = async () => {
    const [s, i, g, l, sup, m] = await Promise.all([
      api.get(`/students/${id}`),
      api.get(`/iep/${id}`),
      api.get(`/goals/${id}`),
      api.get(`/lessons/${id}`),
      api.get(`/supports/${id}`),
      api.get(`/meetings/student/${id}`),
    ]);
    setStudent(s.data);
    setIep(i.data);
    setGoals(g.data);
    setLessons(l.data);
    setSupports(sup.data);
    setMeetings(m.data);
  };

  useEffect(() => { loadAll(); }, [id]);

  if (!student) {
    return <div className="ink-muted calm-pulse">Loading student…</div>;
  }

  return (
    <div className="space-y-8" data-testid="student-detail-page">
      <div>
        <Link to="/students" className="text-sm ink-muted hover:text-[#6E5AA0] inline-flex items-center" data-testid="back-to-students">
          <ChevronLeft size={14} className="mr-1" /> Back to students
        </Link>
        <div className="mt-4 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <h1 className="font-heading text-4xl font-medium ink">{student.first_name} {student.last_name}</h1>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <Badge className="bg-[#EFEAF5] text-[#6E5AA0] hover:bg-[#EFEAF5]">Grade {student.grade}</Badge>
              {student.disability_category && <Badge variant="outline">{student.disability_category}</Badge>}
              {student.case_manager && <span className="text-sm ink-muted">CM: {student.case_manager}</span>}
            </div>
            {student.academic_level && <p className="ink-muted mt-3 max-w-2xl">{student.academic_level}</p>}
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#E8E9E6] rounded-xl p-1 h-auto flex-wrap">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.id}
              value={t.id}
              data-testid={`tab-${t.id}`}
              className="rounded-lg px-4 py-2 data-[state=active]:bg-[#6E5AA0] data-[state=active]:text-white"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="iep" className="mt-6">
          <IEPBuilder student={student} existingIEP={iep} onSaved={loadAll} />
          {iep && (
            <div className="mt-6 text-xs ink-muted">Last saved: {new Date(iep.created_at).toLocaleString()}</div>
          )}
        </TabsContent>

        <TabsContent value="goals" className="mt-6">
          <GoalsTab studentId={id} goals={goals} reload={loadAll} />
        </TabsContent>

        <TabsContent value="lessons" className="mt-6">
          <LessonsTab student={student} goals={goals} lessons={lessons} reload={loadAll} />
        </TabsContent>

        <TabsContent value="meetings" className="mt-6">
          <MeetingsTab student={student} meetings={meetings} reload={loadAll} />
        </TabsContent>

        <TabsContent value="supports" className="mt-6">
          <SupportsTab studentId={id} supports={supports} reload={loadAll} />
        </TabsContent>

        <TabsContent value="progress" className="mt-6">
          <ProgressTab goals={goals} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ----------- Goals ----------- */
function GoalsTab({ studentId, goals, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ area: "", description: "", target_criteria: "", baseline: "" });
  const [progressOpen, setProgressOpen] = useState(null);

  const save = async () => {
    if (!form.area || !form.description) return toast.error("Area and description required");
    await api.post("/goals", { student_id: studentId, ...form });
    setOpen(false);
    setForm({ area: "", description: "", target_criteria: "", baseline: "" });
    reload();
    toast.success("Goal added");
  };

  const remove = async (goalId) => {
    await api.delete(`/goals/${goalId}`);
    toast.success("Goal removed");
    reload();
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="ink-muted text-sm">Track measurable annual goals. AI-generated goals from the IEP appear here automatically.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="add-goal-button"><Plus size={14} className="mr-2" /> Add goal</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New goal</DialogTitle></DialogHeader>
            <div className="grid gap-4">
              <div className="space-y-2"><Label>Area</Label><Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} data-testid="goal-area" /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="goal-description" /></div>
              <div className="space-y-2"><Label>Target criteria</Label><Input value={form.target_criteria} onChange={(e) => setForm({ ...form, target_criteria: e.target.value })} data-testid="goal-criteria" /></div>
              <div className="space-y-2"><Label>Baseline</Label><Input value={form.baseline} onChange={(e) => setForm({ ...form, baseline: e.target.value })} data-testid="goal-baseline" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="goal-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {goals.length === 0 ? (
        <Card className="p-10 border-dashed border-[#D8D9D5] bg-white text-center">
          <p className="ink-muted">No goals yet. Save an IEP to auto-create goals, or add one manually.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {goals.map((g) => (
            <Card key={g.id} className="p-6 border border-[#E8E9E6] bg-white" data-testid={`goal-card-${g.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <Badge variant="outline" className="bg-[#EFEAF5]">{g.area}</Badge>
                  <p className="ink mt-3 font-medium">{g.description}</p>
                  {g.target_criteria && <p className="text-sm ink-muted mt-2">Criteria: {g.target_criteria}</p>}
                  {g.baseline && <p className="text-sm ink-muted">Baseline: {g.baseline}</p>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setProgressOpen(g)} data-testid={`log-progress-${g.id}`}>
                    <TrendingUp size={14} className="mr-1" /> Log progress
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(g.id)} className="text-[#BA6356]">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {progressOpen && (
        <ProgressDialog goal={progressOpen} onClose={() => { setProgressOpen(null); reload(); }} />
      )}
    </div>
  );
}

function ProgressDialog({ goal, onClose }) {
  const [score, setScore] = useState("");
  const [note, setNote] = useState("");
  const save = async () => {
    const s = parseFloat(score);
    if (isNaN(s) || s < 0 || s > 100) return toast.error("Enter a number between 0 and 100");
    await api.post("/goals/progress", { goal_id: goal.id, score: s, note });
    toast.success("Progress logged");
    onClose();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Log progress — {goal.area}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div className="space-y-2"><Label>Score (0–100)</Label><Input type="number" value={score} onChange={(e) => setScore(e.target.value)} data-testid="progress-score" /></div>
          <div className="space-y-2"><Label>Note</Label><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="progress-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="progress-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------- Lessons ----------- */
function LessonsTab({ student, goals, lessons, reload }) {
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [standards, setStandards] = useState("");
  const [academicLevel, setAcademicLevel] = useState(student.academic_level || "");
  const [count, setCount] = useState(3);
  const [linkedGoal, setLinkedGoal] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", standard: "", objective: "", materials: "", activities: "", assessment: "", duration_minutes: 30, goal_id: "" });

  const suggest = async () => {
    if (!standards) return toast.error("Enter the curriculum standards to align with");
    setLoading(true);
    try {
      const r = await api.post("/lessons/suggest", {
        student_id: student.id,
        goal_id: linkedGoal || null,
        standards,
        academic_level: academicLevel,
        count,
      });
      if (r.data.parsed && Array.isArray(r.data.parsed)) {
        setSuggestions(r.data.parsed);
      } else {
        toast.error("AI couldn't return a structured list. Try refining your standards.");
      }
    } catch (e) {
      toast.error("Could not generate suggestions");
    } finally {
      setLoading(false);
    }
  };

  const acceptSuggestion = async (s) => {
    await api.post("/lessons", {
      student_id: student.id,
      goal_id: linkedGoal || null,
      title: s.title,
      standard: s.standard,
      objective: s.objective,
      materials: s.materials,
      activities: s.activities,
      assessment: s.assessment,
      duration_minutes: s.duration_minutes,
      source: "ai",
    });
    toast.success("Lesson added");
    reload();
  };

  const saveManual = async () => {
    if (!form.title) return toast.error("Title required");
    setAdding(true);
    try {
      await api.post("/lessons", { student_id: student.id, source: "teacher", ...form, goal_id: form.goal_id || null });
      toast.success("Lesson added");
      setAddOpen(false);
      setForm({ title: "", standard: "", objective: "", materials: "", activities: "", assessment: "", duration_minutes: 30, goal_id: "" });
      reload();
    } finally {
      setAdding(false);
    }
  };

  const removeLesson = async (lid) => {
    await api.delete(`/lessons/${lid}`);
    toast.success("Lesson removed");
    reload();
  };

  const markComplete = async (lesson) => {
    const scoreStr = window.prompt("Score (0-100), or leave blank:");
    let score = null;
    if (scoreStr && scoreStr.trim() !== "") {
      score = parseFloat(scoreStr);
      if (isNaN(score)) { toast.error("Invalid score"); return; }
    }
    await api.post("/lessons/completion", { lesson_id: lesson.id, status: "completed", score });
    toast.success("Lesson marked complete");
    reload();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="ink-muted text-sm max-w-xl">AI suggests lessons aligned to curriculum standards and {student.first_name}'s academic level. You can also add your own.</p>
        <div className="flex gap-2">
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-lesson-button"><Plus size={14} className="mr-2" /> Add lesson</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>New lesson</DialogTitle></DialogHeader>
              <div className="grid gap-3 max-h-[60vh] overflow-y-auto pr-2">
                <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="lesson-title" /></div>
                <div className="space-y-2"><Label>Standard</Label><Input value={form.standard} onChange={(e) => setForm({ ...form, standard: e.target.value })} data-testid="lesson-standard" /></div>
                <div className="space-y-2"><Label>Objective</Label><Textarea rows={2} value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} data-testid="lesson-objective" /></div>
                <div className="space-y-2"><Label>Materials</Label><Textarea rows={2} value={form.materials} onChange={(e) => setForm({ ...form, materials: e.target.value })} /></div>
                <div className="space-y-2"><Label>Activities</Label><Textarea rows={3} value={form.activities} onChange={(e) => setForm({ ...form, activities: e.target.value })} /></div>
                <div className="space-y-2"><Label>Assessment</Label><Textarea rows={2} value={form.assessment} onChange={(e) => setForm({ ...form, assessment: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Duration (minutes)</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value || 0) })} /></div>
                  <div className="space-y-2">
                    <Label>Link to goal (optional)</Label>
                    <Select value={form.goal_id} onValueChange={(v) => setForm({ ...form, goal_id: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {goals.map((g) => <SelectItem key={g.id} value={g.id}>{g.area} — {g.description.slice(0, 40)}…</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button onClick={saveManual} disabled={adding} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="lesson-save">Save lesson</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={suggestOpen} onOpenChange={setSuggestOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="suggest-lessons-button">
                <Sparkles size={14} className="mr-2" /> AI suggest lessons
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>AI lesson suggestions</DialogTitle></DialogHeader>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label>Curriculum standards</Label>
                  <Textarea rows={3} placeholder="Paste or list the standards (e.g., CCSS.ELA-LITERACY.RL.3.1, RL.3.2…)" value={standards} onChange={(e) => setStandards(e.target.value)} data-testid="suggest-standards" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2"><Label>Academic level</Label><Input value={academicLevel} onChange={(e) => setAcademicLevel(e.target.value)} data-testid="suggest-level" /></div>
                  <div className="space-y-2"><Label>How many</Label><Input type="number" min={1} max={5} value={count} onChange={(e) => setCount(parseInt(e.target.value || 3))} /></div>
                  <div className="space-y-2">
                    <Label>Link to goal</Label>
                    <Select value={linkedGoal} onValueChange={(v) => setLinkedGoal(v === "none" ? "" : v)}>
                      <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {goals.map((g) => <SelectItem key={g.id} value={g.id}>{g.area}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={suggest} disabled={loading} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="suggest-go">
                  {loading ? <span className="calm-pulse">Drafting lessons…</span> : <><Sparkles size={14} className="mr-2" /> Generate</>}
                </Button>

                {suggestions.length > 0 && (
                  <div className="space-y-3 mt-2 max-h-[40vh] overflow-y-auto">
                    {suggestions.map((s, i) => (
                      <Card key={i} className="p-4 border border-[#E8E9E6] bg-[#FBFBF9]">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="font-medium ink">{s.title}</div>
                            <div className="text-xs ink-muted mt-1">{s.standard} · {s.duration_minutes} min</div>
                            <div className="text-sm ink mt-2">{s.objective}</div>
                          </div>
                          <Button size="sm" onClick={() => acceptSuggestion(s)} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid={`accept-suggestion-${i}`}>Add</Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {lessons.length === 0 ? (
        <Card className="p-10 border-dashed border-[#D8D9D5] bg-white text-center">
          <p className="ink-muted">No lessons yet. Use AI to draft some aligned to your standards, or add your own.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lessons.map((l) => (
            <Card key={l.id} className="p-6 border border-[#E8E9E6] bg-white" data-testid={`lesson-card-${l.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Badge variant="outline" className="bg-[#EFEAF5]">{l.source === "ai" ? "AI" : "Teacher"}</Badge>
                  <div className="font-heading text-lg font-medium ink mt-3">{l.title}</div>
                  {l.standard && <div className="text-xs ink-muted mt-1">{l.standard}</div>}
                  {l.objective && <p className="text-sm ink mt-3">{l.objective}</p>}
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => markComplete(l)} data-testid={`complete-lesson-${l.id}`}>
                  <CheckCircle2 size={14} className="mr-1" /> Mark complete
                </Button>
                <Button variant="ghost" size="sm" onClick={() => removeLesson(l.id)} className="text-[#BA6356]">
                  <Trash2 size={14} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------- Meetings ----------- */
function MeetingsTab({ student, meetings, reload }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ title: "Annual IEP Review", scheduled_at: "", duration_minutes: 60, attendees: "", location: "" });

  const create = async () => {
    if (!form.title || !form.scheduled_at) return toast.error("Title and date required");
    await api.post("/meetings", {
      student_id: student.id,
      title: form.title,
      scheduled_at: new Date(form.scheduled_at).toISOString(),
      duration_minutes: form.duration_minutes,
      attendees: form.attendees.split(",").map((s) => s.trim()).filter(Boolean),
      location: form.location,
    });
    toast.success("Meeting scheduled");
    setOpen(false);
    setForm({ title: "Annual IEP Review", scheduled_at: "", duration_minutes: 60, attendees: "", location: "" });
    reload();
  };

  const remove = async (mid) => {
    await api.delete(`/meetings/${mid}`);
    toast.success("Meeting removed");
    reload();
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="ink-muted text-sm">Schedule meetings, generate AI-guided agendas, and capture live notes during the meeting.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="add-meeting-button"><Plus size={14} className="mr-2" /> Schedule meeting</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New meeting</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="meeting-title" /></div>
              <div className="space-y-2"><Label>Date & time</Label><Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="meeting-datetime" /></div>
              <div className="space-y-2"><Label>Duration (minutes)</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value || 60) })} /></div>
              <div className="space-y-2"><Label>Attendees (comma-separated)</Label><Input value={form.attendees} onChange={(e) => setForm({ ...form, attendees: e.target.value })} placeholder="Parent, Principal, SLP, …" data-testid="meeting-attendees" /></div>
              <div className="space-y-2"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="meeting-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {meetings.length === 0 ? (
        <Card className="p-10 border-dashed border-[#D8D9D5] bg-white text-center">
          <p className="ink-muted">No meetings yet. Schedule one to get an AI-generated agenda.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {meetings.map((m) => (
            <Card key={m.id} className="p-6 border border-[#E8E9E6] bg-white" data-testid={`meeting-card-${m.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="font-heading text-xl font-medium ink">{m.title}</div>
                  <div className="text-xs ink-muted mt-1">{new Date(m.scheduled_at).toLocaleString()} · {m.duration_minutes} min · {m.location || "Location TBD"}</div>
                  {m.attendees?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {m.attendees.map((a, i) => <Badge key={i} variant="outline" className="bg-[#EFEAF5]">{a}</Badge>)}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setActive(m)} data-testid={`open-meeting-${m.id}`}>Open</Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(m.id)} className="text-[#BA6356]"><Trash2 size={14} /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {active && <MeetingDialog meeting={active} onClose={() => { setActive(null); reload(); }} />}
    </div>
  );
}

/* ----------- Supports ----------- */
function SupportsTab({ studentId, supports, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "accommodation", title: "", description: "", frequency: "" });

  const grouped = {
    accommodation: supports.filter((s) => s.category === "accommodation"),
    modification: supports.filter((s) => s.category === "modification"),
    related_service: supports.filter((s) => s.category === "related_service"),
  };

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    await api.post("/supports", { student_id: studentId, ...form });
    setOpen(false);
    setForm({ category: "accommodation", title: "", description: "", frequency: "" });
    reload();
    toast.success("Support added");
  };

  const remove = async (sid) => {
    await api.delete(`/supports/${sid}`);
    reload();
  };

  const labels = { accommodation: "Accommodations", modification: "Modifications", related_service: "Related services" };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="ink-muted text-sm">A quick reference of all the supports that follow this student into every classroom.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="add-support-button"><Plus size={14} className="mr-2" /> Add support</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New support</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="support-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accommodation">Accommodation</SelectItem>
                    <SelectItem value="modification">Modification</SelectItem>
                    <SelectItem value="related_service">Related service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="support-title" /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="space-y-2"><Label>Frequency</Label><Input value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} placeholder="e.g., daily, weekly, as needed" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-[#6E5AA0] hover:bg-[#574785] text-white" data-testid="support-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {Object.entries(grouped).map(([cat, items]) => (
          <Card key={cat} className="p-6 border border-[#E8E9E6] bg-white">
            <div className="overline">{labels[cat]}</div>
            <div className="font-heading text-3xl font-medium ink mt-1">{items.length}</div>
            <div className="mt-4 space-y-2">
              {items.length === 0 && <div className="text-sm ink-muted">No items yet.</div>}
              {items.map((s) => (
                <div key={s.id} className="flex items-center justify-between border border-[#EDEDE9] rounded-lg px-3 py-2 text-sm bg-[#FBFBF9]" data-testid={`support-row-${s.id}`}>
                  <div>
                    <div className="ink">{s.title}</div>
                    {s.frequency && <div className="text-xs ink-muted">{s.frequency}</div>}
                  </div>
                  <button onClick={() => remove(s.id)} className="text-[#BA6356] text-xs">remove</button>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ----------- Progress ----------- */
function ProgressTab({ goals }) {
  const [data, setData] = useState({});
  const [selected, setSelected] = useState(goals[0]?.id || "");

  useEffect(() => {
    if (!selected && goals[0]) setSelected(goals[0].id);
  }, [goals, selected]);

  useEffect(() => {
    if (!selected) return;
    api.get(`/goals/${selected}/progress`).then((r) => {
      setData((prev) => ({ ...prev, [selected]: r.data }));
    });
  }, [selected]);

  if (goals.length === 0) {
    return (
      <Card className="p-10 border-dashed border-[#D8D9D5] bg-white text-center">
        <p className="ink-muted">Add goals first, then progress data will graph here.</p>
      </Card>
    );
  }

  const chartData = (data[selected] || []).map((d) => ({
    date: new Date(d.date).toLocaleDateString(),
    score: d.score,
  }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {goals.map((g) => (
          <button
            key={g.id}
            onClick={() => setSelected(g.id)}
            data-testid={`progress-goal-${g.id}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-all ${selected === g.id ? "bg-[#6E5AA0] text-white border-[#6E5AA0]" : "bg-white text-[#2D322F] border-[#E8E9E6] hover:bg-[#EFEAF5]"}`}
          >
            {g.area}
          </button>
        ))}
      </div>

      <Card className="p-6 sm:p-8 border border-[#E8E9E6] bg-white">
        {chartData.length === 0 ? (
          <div className="ink-muted text-sm py-12 text-center">No data points yet for this goal.</div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 16, bottom: 10, left: 0 }}>
                <CartesianGrid stroke="#E8E9E6" vertical={false} />
                <XAxis dataKey="date" stroke="#6B726F" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 100]} stroke="#6B726F" tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="#6E5AA0" strokeWidth={2.5} dot={{ r: 4, fill: "#6E5AA0" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </div>
  );
}
